import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  TrendingUp, Shield, FileText, Minus, ArrowUpRight, ArrowDownRight, Loader2,
} from 'lucide-react';

function Delta({ standard, actual, format = 'number', inverse = false, sym = '$' }) {
  if (standard == null || actual == null) return <span className="text-slate-400">&mdash;</span>;
  const diff = actual - standard;
  const pct = standard !== 0 ? ((diff / standard) * 100) : 0;
  if (Math.abs(pct) < 0.5) return <span className="text-slate-400 text-xs flex items-center justify-end gap-0.5"><Minus className="w-3 h-3" /> No change</span>;
  const isUp = diff > 0;
  const isGood = inverse ? isUp : !isUp;
  const color = isGood ? 'text-emerald-600' : 'text-red-600';
  const Icon = isUp ? ArrowUpRight : ArrowDownRight;
  const label = format === 'currency' ? `${sym}${Math.abs(diff).toLocaleString(undefined, {maximumFractionDigits: 0})}` : `${Math.abs(diff).toLocaleString(undefined, {maximumFractionDigits: 0})} hrs`;
  return (
    <span className={`text-xs flex items-center justify-end gap-0.5 ${color}`}>
      <Icon className="w-3 h-3" />{isUp ? '+' : '-'}{label} ({Math.abs(pct).toFixed(1)}%)
    </span>
  );
}

export default function OutputComparisonSection({ results, standardResults, state, grouped, standardGrouped, currencySymbol = '$' }) {
  return (
    <div className="space-y-6" data-testid="section-comparison">
      {/* Top-level KPI comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#14B8A6]" /> Standard vs Actual Estimate
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">Standard uses all neutral modifiers (1.0) and default rate card. Actual reflects your adjustments.</p>
        </CardHeader>
        <CardContent>
          {!standardResults ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="kpi-comparison-table">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-600" style={{width:'28%'}}>Metric</th>
                    <th className="text-right py-3 px-4 font-semibold text-blue-700 bg-blue-50/50" style={{width:'22%'}}>Standard</th>
                    <th className="text-right py-3 px-4 font-semibold text-violet-700 bg-violet-50/50" style={{width:'22%'}}>Actual</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-600" style={{width:'28%'}}>Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Total Effort', std: standardResults.total_effort, act: results.total_effort, fmt: 'number', suffix: ' hrs' },
                    { label: 'Total Cost', std: standardResults.total_cost, act: results.total_cost, fmt: 'currency', prefix: '$' },
                    { label: 'Confidence', std: standardResults.confidence, act: results.confidence, fmt: 'pct', suffix: '%', inverse: true },
                    { label: 'Complexity Score', std: standardResults.complexity_score, act: results.complexity_score, fmt: 'number', suffix: '/100' },
                  ].map(row => (
                    <tr key={row.label} className="border-b border-slate-100">
                      <td className="py-3 px-4 font-medium text-slate-700">{row.label}</td>
                      <td className="py-3 px-4 text-right text-blue-800 font-semibold bg-blue-50/30">{row.prefix || ''}{row.std?.toLocaleString(undefined, {maximumFractionDigits: 0})}{row.suffix || ''}</td>
                      <td className="py-3 px-4 text-right text-violet-800 font-semibold bg-violet-50/30">{row.prefix || ''}{row.act?.toLocaleString(undefined, {maximumFractionDigits: 0})}{row.suffix || ''}</td>
                      <td className="py-3 px-4 text-right"><Delta sym={currencySymbol} standard={row.std} actual={row.act} format={row.fmt === 'currency' ? 'currency' : 'number'} inverse={row.inverse} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modifier Adjustments */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Shield className="w-5 h-5 text-amber-500" /> Modifier Adjustments</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Shows how each modifier deviates from the standard baseline (1.0).</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="modifier-comparison-table">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-2.5 px-4 font-semibold text-slate-600" style={{width:'30%'}}>Modifier</th>
                  <th className="text-center py-2.5 px-4 font-semibold text-blue-700 bg-blue-50/50" style={{width:'15%'}}>Standard</th>
                  <th className="text-center py-2.5 px-4 font-semibold text-violet-700 bg-violet-50/50" style={{width:'15%'}}>Actual</th>
                  <th className="text-left py-2.5 px-4 font-semibold text-slate-600" style={{width:'40%'}}>Impact</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(state.modifiers || {}).map(([key, val]) => {
                  const labels = {
                    governance: 'Governance', content_readiness: 'Content Readiness',
                    integration_complexity: 'Integration Complexity', org_complexity: 'Org Complexity',
                    timeline_pressure: 'Timeline Pressure', client_capability: 'Client Capability',
                    change_readiness: 'Change Readiness'
                  };
                  const isNeutral = Math.abs(val - 1.0) < 0.01;
                  const isGood = val < 1.0;
                  const pctChange = ((val - 1.0) * 100).toFixed(0);
                  const barWidth = Math.min(Math.abs(val - 1.0) * 200, 100);
                  return (
                    <tr key={key} className="border-b border-slate-100" data-testid={`modifier-compare-${key}`}>
                      <td className="py-2.5 px-4 text-slate-700 font-medium">{labels[key] || key}</td>
                      <td className="py-2.5 px-4 text-center text-blue-700 bg-blue-50/30 font-mono">1.00</td>
                      <td className={`py-2.5 px-4 text-center font-mono font-semibold bg-violet-50/30 ${isNeutral ? 'text-slate-600' : isGood ? 'text-emerald-700' : 'text-red-700'}`}>{val.toFixed(2)}</td>
                      <td className="py-2.5 px-4">
                        {isNeutral ? (
                          <span className="text-xs text-slate-400 flex items-center gap-1"><Minus className="w-3 h-3" /> No change</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden relative">
                              <div className={`h-full rounded-full ${isGood ? 'bg-emerald-500' : 'bg-red-400'}`} style={{ width: `${barWidth}%` }} />
                            </div>
                            <span className={`text-xs font-semibold whitespace-nowrap ${isGood ? 'text-emerald-700' : 'text-red-700'}`}>{isGood ? '' : '+'}{pctChange}%</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Component-Level Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><FileText className="w-5 h-5 text-[#0F2040]" /> Component-Level Comparison</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Per-component breakdown with group subtotals.</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="component-comparison-table">
              <thead>
                <tr className="border-b-2 border-slate-200 bg-slate-50">
                  <th className="text-left py-2.5 px-3 font-semibold text-slate-600" style={{width:'26%'}}>Component</th>
                  <th className="text-right py-2.5 px-3 font-semibold text-blue-700" style={{width:'12%'}}>Std Hours</th>
                  <th className="text-right py-2.5 px-3 font-semibold text-violet-700" style={{width:'12%'}}>Act Hours</th>
                  <th className="text-right py-2.5 px-3 font-semibold text-slate-600" style={{width:'12%'}}>Hrs Delta</th>
                  <th className="text-right py-2.5 px-3 font-semibold text-blue-700" style={{width:'12%'}}>Std Cost</th>
                  <th className="text-right py-2.5 px-3 font-semibold text-violet-700" style={{width:'12%'}}>Act Cost</th>
                  <th className="text-right py-2.5 px-3 font-semibold text-slate-600" style={{width:'14%'}}>Cost Delta</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([group, comps]) => {
                  const groupStdHours = comps.reduce((sum, c) => sum + (standardGrouped[group]?.[c.name]?.final_effort || 0), 0);
                  const groupActHours = comps.reduce((sum, c) => sum + (c.final_effort || 0), 0);
                  const groupStdCost = comps.reduce((sum, c) => sum + (standardGrouped[group]?.[c.name]?.cost || 0), 0);
                  const groupActCost = comps.reduce((sum, c) => sum + (c.cost || 0), 0);
                  return (
                    <React.Fragment key={group}>
                      <tr className="bg-[#0F2040]/[0.04]" data-testid={`group-heading-${group.replace(/\s/g,'-').toLowerCase()}`}>
                        <td colSpan={7} className="py-2 px-3 font-bold text-xs text-[#0F2040] uppercase tracking-wider border-b-2 border-[#0F2040]/10">
                          {group}<span className="ml-2 font-normal text-slate-400">({comps.length} items)</span>
                        </td>
                      </tr>
                      {comps.map(comp => {
                        const stdComp = standardGrouped[group]?.[comp.name];
                        return (
                          <tr key={comp.name} className="border-b border-slate-100 hover:bg-slate-50/60">
                            <td className="py-2 px-3 text-slate-700 pl-5">{comp.name}</td>
                            <td className="py-2 px-3 text-right text-blue-700 font-mono">{stdComp?.final_effort?.toFixed(0) || '\u2014'}</td>
                            <td className="py-2 px-3 text-right text-violet-700 font-mono">{comp.final_effort?.toFixed(0)}</td>
                            <td className="py-2 px-3 text-right"><Delta sym={currencySymbol} standard={stdComp?.final_effort} actual={comp.final_effort} /></td>
                            <td className="py-2 px-3 text-right text-blue-700 font-mono">{currencySymbol}{stdComp?.cost?.toFixed(0) || '\u2014'}</td>
                            <td className="py-2 px-3 text-right text-violet-700 font-mono">{currencySymbol}{comp.cost?.toFixed(0)}</td>
                            <td className="py-2 px-3 text-right"><Delta sym={currencySymbol} standard={stdComp?.cost} actual={comp.cost} format="currency" /></td>
                          </tr>
                        );
                      })}
                      <tr className="bg-slate-50 border-b-2 border-slate-200" data-testid={`group-subtotal-${group.replace(/\s/g,'-').toLowerCase()}`}>
                        <td className="py-2 px-3 text-xs font-bold text-slate-500 uppercase pl-5">Subtotal</td>
                        <td className="py-2 px-3 text-right text-blue-800 font-bold font-mono">{groupStdHours.toFixed(0)}</td>
                        <td className="py-2 px-3 text-right text-violet-800 font-bold font-mono">{groupActHours.toFixed(0)}</td>
                        <td className="py-2 px-3 text-right"><Delta sym={currencySymbol} standard={groupStdHours} actual={groupActHours} /></td>
                        <td className="py-2 px-3 text-right text-blue-800 font-bold font-mono">{currencySymbol}{groupStdCost.toFixed(0)}</td>
                        <td className="py-2 px-3 text-right text-violet-800 font-bold font-mono">{currencySymbol}{groupActCost.toFixed(0)}</td>
                        <td className="py-2 px-3 text-right"><Delta sym={currencySymbol} standard={groupStdCost} actual={groupActCost} format="currency" /></td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                {/* Grand total */}
                {(() => {
                  const allComps = Object.values(grouped).flat();
                  const grandStdHrs = allComps.reduce((s, c) => {
                    const g = (results.component_breakdown || []).find(x => x.name === c.name)?.group;
                    return s + (standardGrouped[g]?.[c.name]?.final_effort || 0);
                  }, 0);
                  const grandActHrs = allComps.reduce((s, c) => s + (c.final_effort || 0), 0);
                  const grandStdCost = allComps.reduce((s, c) => {
                    const g = (results.component_breakdown || []).find(x => x.name === c.name)?.group;
                    return s + (standardGrouped[g]?.[c.name]?.cost || 0);
                  }, 0);
                  const grandActCost = allComps.reduce((s, c) => s + (c.cost || 0), 0);
                  return (
                    <tr className="bg-[#0F2040] text-white" data-testid="grand-total-row">
                      <td className="py-3 px-3 font-bold text-sm pl-5">Grand Total</td>
                      <td className="py-3 px-3 text-right font-bold font-mono">{grandStdHrs.toFixed(0)}</td>
                      <td className="py-3 px-3 text-right font-bold font-mono">{grandActHrs.toFixed(0)}</td>
                      <td className="py-3 px-3 text-right">
                        <span className={`text-xs font-semibold ${grandActHrs - grandStdHrs > 0 ? 'text-red-300' : grandActHrs - grandStdHrs < 0 ? 'text-emerald-300' : 'text-white/60'}`}>
                          {grandActHrs - grandStdHrs > 0 ? '+' : ''}{(grandActHrs - grandStdHrs).toFixed(0)} hrs
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right font-bold font-mono">{currencySymbol}{grandStdCost.toFixed(0)}</td>
                      <td className="py-3 px-3 text-right font-bold font-mono">{currencySymbol}{grandActCost.toFixed(0)}</td>
                      <td className="py-3 px-3 text-right">
                        <span className={`text-xs font-semibold ${grandActCost - grandStdCost > 0 ? 'text-red-300' : grandActCost - grandStdCost < 0 ? 'text-emerald-300' : 'text-white/60'}`}>
                          {grandActCost - grandStdCost > 0 ? '+' : ''}${Math.abs(grandActCost - grandStdCost).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </span>
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
